import styled, { css } from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import { SplitRow } from '../ui';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  & > * {
    min-width: 0;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SummarySubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const SuccessNotice = styled.div`
  font-size: 13px;
  color: #10b981;
  font-weight: 600;
`;

export const ErrorNotice = styled.div`
  font-size: 13px;
  color: #ef4444;
  font-weight: 600;
`;

export const AccountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 14px;
`;

/** Счета компании: у одной компании их часто 1–2, такая карточка не тянется на всю ширину. */
export const CompanyAccountsGrid = styled(AccountsGrid)`
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
`;

export const AccountBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const AccNumber = styled.div`
  font-size: 12px;
  font-family: monospace;
  opacity: 0.75;
  overflow-wrap: anywhere;
`;

export const Balance = styled.div`
  margin-top: 4px;
  font-size: 22px;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

export const SyncRow = styled(SplitRow)`
  align-items: center;
  margin-top: 6px;
  font-size: 11px;
  opacity: 0.75;
`;

export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const Input = styled.input`
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const Select = styled.select`
  min-width: 0;
  max-width: 100%;
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

/** Таблица в своей области прокрутки: у длинного списка обе полосы прокрутки видны в окне,
 *  шапка остаётся на месте. Короткая таблица — по высоте содержимого. */
export const TableWrap = styled.div`
  max-height: min(70vh, calc(100vh - 260px));
  overflow: auto;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.backgroundElevated};
    /* Линия под закреплённой шапкой: рамка ячейки при прокрутке уезжает вместе с таблицей. */
    box-shadow: inset 0 -1px 0 ${({ theme }) => theme.colors.border};
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

/** Операции дня: узкие колонки даты, счёта и источника, назначение забирает остаток. */
export const PaymentsTable = styled(Table)`
  td {
    vertical-align: top;
  }
  th:nth-child(1) {
    width: 104px;
  }
  th:nth-child(2) {
    width: 150px;
  }
  th:nth-child(3) {
    min-width: 180px;
  }
  th:nth-child(4) {
    min-width: 240px;
  }
  th:nth-child(5) {
    width: 160px;
  }
  th:nth-child(7) {
    width: 96px;
  }
`;

export const TableNoticeCell = styled.td`
  text-align: center;
  padding: 20px 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Значок в ячейке таблицы: длинная подпись переносится, а не растягивает колонку. */
export const CellBadge = styled(Badge)`
  white-space: normal;
  overflow-wrap: anywhere;
  text-transform: none;
  letter-spacing: normal;
  line-height: 1.3;
`;

export const OwnerLine = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const MissingRequisites = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export const FormHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const HintSteps = styled.ol`
  margin: 6px 0;
  padding-left: 20px;

  li + li {
    margin-top: 4px;
  }
`;

export const FormBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FormTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(210px, 100%), 1fr));
  gap: 10px;
`;

export const CheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

export const StatementTextarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: transparent;
  color: inherit;
  font-family: monospace;
  font-size: 12px;
`;

export const SubTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ConnectionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export const ConnectionError = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.status.warning};
  overflow-wrap: anywhere;
`;

export const UploadWarning = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export const CompanyTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
`;

export const AccountTypeLabel = styled.div`
  font-size: 12px;
  opacity: 0.85;
`;

export const BlockedAmountText = styled.span`
  color: #f59e0b;
`;

export const PurposeText = styled.div`
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
`;

export const DailySummarySubtext = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
`;

export const InflowTotalText = styled.strong`
  color: #10b981;
`;

export const OutflowTotalText = styled.strong`
  color: #ef4444;
`;

export const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
`;

export const FilterHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Кнопка с пояснением под ней, почему она недоступна. */
export const ActionWithHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  max-width: 320px;
`;

export const ActionHint = styled.div`
  font-size: 11px;
  text-align: right;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Поле формы: подпись над полем, под ним — ошибка этого поля. */
export const FieldBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  & > input,
  & > select {
    width: 100%;
    box-sizing: border-box;
  }
`;

export const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const FieldError = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ef4444;
`;

/** Что сделает ручная запись: видно до сохранения. */
export const ManualNotice = styled.div`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  background: ${({ theme }) => theme.colors.status.warning}14;
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const DocDateText = styled.div`
  font-weight: 700;
`;

export const DocNumberText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const AccountMonoText = styled.div`
  font-size: 11px;
  font-family: monospace;
  opacity: 0.7;
`;

export const CounterpartyNameText = styled.div`
  font-weight: 700;
`;

export const CounterpartyInnText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const PurposeCell = styled.td`
  max-width: 420px;
  overflow-wrap: anywhere;
`;

/** Назначение платежа в две строки; полностью — по кнопке «ещё». */
export const PurposeClamp = styled.div<{ $expanded: boolean }>`
  ${({ $expanded }) =>
    !$expanded &&
    css`
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      overflow: hidden;
    `}
`;

export const MoreButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const AmountCell = styled.td<{ $direction?: string }>`
  font-weight: 800;
  white-space: nowrap;
  color: ${({ $direction }) => ($direction === 'inflow' ? '#10b981' : '#ef4444')};
`;

export const SourceText = styled.span`
  font-size: 11px;
  opacity: 0.75;
`;

export const HistorySubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const DateCell = styled.td`
  font-weight: 700;
`;

export const InflowHistoryCell = styled.td<{ $hasInflow?: boolean }>`
  color: ${({ $hasInflow }) => ($hasInflow ? '#10b981' : 'inherit')};
`;

export const OutflowHistoryCell = styled.td<{ $hasOutflow?: boolean }>`
  color: ${({ $hasOutflow }) => ($hasOutflow ? '#ef4444' : 'inherit')};
`;

export const ClosingBalanceCell = styled.td`
  font-weight: 800;
`;
